import type { Locale } from '../../content/site'

type Message = { en: string; ru: string }

/**
 * Every user-facing string the shell can print. Command *output* that a real
 * Unix tool would emit in English (error strings, `uname`, man page headers)
 * deliberately stays untranslated — a bilingual `ls` would break the illusion.
 * Only prose written by the site is localised.
 */
export const messages = {
  bootHint: {
    en: "Type `help` for commands, or `man <command>` for details. Tab completes.",
    ru: 'Введите `help` для списка команд или `man <команда>` для подробностей. Tab дополняет.',
  },
  bootLast: {
    en: 'Last login: never — you are a guest on this machine.',
    ru: 'Последний вход: никогда — вы гость на этой машине.',
  },
  bootReady: {
    en: '{n} commands available.',
    ru: 'Доступно команд: {n}.',
  },
  bootNow: { en: 'now: ', ru: 'сейчас: ' },
  bootNowHint: { en: '· `work` for more', ru: '· подробнее — `work`' },
  helpTitle: { en: 'available commands', ru: 'доступные команды' },
  helpFooter: {
    en: 'man <command> · Ctrl+R searches history · Ctrl+L clears · Tab completes',
    ru: 'man <команда> · Ctrl+R — поиск по истории · Ctrl+L — очистить · Tab — дополнение',
  },
  projectsLoading: { en: 'loading project index…', ru: 'загрузка списка проектов…' },
  projectsFailed: {
    en: 'project index unavailable — the API did not respond',
    ru: 'список проектов недоступен — API не ответил',
  },
  projectsEmpty: { en: 'no published projects', ru: 'нет опубликованных проектов' },
  stack: { en: 'stack', ru: 'стек' },
  preview: { en: 'preview', ru: 'превью' },
  langSwitched: { en: 'language set to English', ru: 'язык переключён на русский' },
  themeUsage: {
    en: 'Usage: theme <list|set <name>|reset>',
    ru: 'Использование: theme <list|set <имя>|reset>',
  },
  themeApplied: { en: 'theme applied', ru: 'тема применена' },
  themeReset: { en: 'theme reset to default', ru: 'тема сброшена на стандартную' },
  sudoLecture: {
    en: 'We trust you have received the usual lecture from the local System Administrator.',
    ru: 'Мы полагаем, что вы уже выслушали лекцию от локального системного администратора.',
  },
  sudoDenied: {
    en: 'mk69 is not in the sudoers file. This incident will be reported.',
    ru: 'mk69 отсутствует в файле sudoers. Об этом инциденте будет доложено.',
  },
  editorHint: {
    en: 'this terminal has no framebuffer — the file is read-only, use `cat`',
    ru: 'у этого терминала нет фреймбуфера — файл только для чтения, используйте `cat`',
  },
  exitHint: {
    en: 'there is no way out. closing the tab works, though.',
    ru: 'выхода нет. хотя закрыть вкладку всё же можно.',
  },
  rmDenied: {
    en: 'permission denied — this filesystem is served over HTTPS and is read-only',
    ru: 'отказано в доступе — эта файловая система только для чтения',
  },
  contactHint: {
    en: 'fastest reply is Telegram.',
    ru: 'быстрее всего отвечаю в Telegram.',
  },
  interrupted: { en: 'interrupted', ru: 'прервано' },
  guiHint: {
    en: 'the window opens on top of this terminal. `Esc` or the close button quits it.',
    ru: 'окно откроется поверх терминала. `Esc` или кнопка закрытия завершат его.',
  },
  guiSuspended: {
    en: 'type `fg` to bring the window back.',
    ru: 'введите `fg`, чтобы вернуть окно.',
  },
  guiExited: { en: 'exited with code 0', ru: 'завершено с кодом 0' },
  guiTip: {
    en: 'tip: `portfolio` opens the same catalogue in a GUI window.',
    ru: 'подсказка: `portfolio` откроет этот же каталог в GUI-окне.',
  },
} as const satisfies Record<string, Message>

export type MessageKey = keyof typeof messages

export const t = (key: MessageKey, locale: Locale): string => messages[key][locale]
